import { Menu, Wallet, PanelLeftOpen, PanelLeftClose, Lock } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { useQuery } from '@tanstack/react-query'
import { dashboardAPI } from '../../api/dashboard'
import { formatCurrency, parseHeldAmount } from '../../utils/helpers'
import Skeleton from '../ui/Skeleton'

const Header = ({ onMenuClick, onDesktopSidebarToggle, isDesktopSidebarOpen = true }) => {
  const { isAuthenticated } = useAuth()

  // Fetch merchant name and balances
  const { data: merchantData, isLoading } = useQuery({
    queryKey: ['merchant-name'],
    queryFn: dashboardAPI.getMerchantName,
    enabled: isAuthenticated,
    retry: 1,
    refetchOnWindowFocus: true,
    staleTime: 60 * 1000,
    cacheTime: 5 * 60 * 1000, // 5 minutes
  })

  if (!isAuthenticated) return null

  return (
    <header className="sticky top-0 z-30 bg-white border-b border-gray-200">
      <div className="flex items-center justify-between px-4 py-3 lg:px-6 gap-4">
        <div className="flex items-center gap-2">
          {/* Mobile Menu Button */}
          <button
            onClick={onMenuClick}
            className="lg:hidden p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
          >
            <Menu size={24} />
          </button>
          
          {/* Desktop Sidebar Toggle Button */}
          <button
            onClick={onDesktopSidebarToggle}
            className="hidden lg:flex p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
            title={isDesktopSidebarOpen ? 'Hide Sidebar' : 'Show Sidebar'}
          >
            {isDesktopSidebarOpen ? (
              <PanelLeftClose size={24} />
            ) : (
              <PanelLeftOpen size={24} />
            )}
          </button>
        </div>
        
        {/* Merchant Name - Centered */}
        <div className="flex-1 flex justify-center min-w-0">
          {isLoading ? (
            <Skeleton variant="text" className="w-48 h-6" />
          ) : (
            <h2 className="text-lg font-bold text-gray-900 text-center">
              👋 Hello {merchantData?.merchantName || 'Merchant'} {merchantData?.merchantId && `(${merchantData.merchantId})`}
            </h2>
          )}
        </div>

        {/* Wallet balances + held (server nets hold into payout figure; do not subtract again) */}
        <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3 flex-shrink-0 max-w-[min(100%,52rem)]">
          {isLoading ? (
            <>
              <Skeleton variant="text" className="w-24 sm:w-32 h-12" />
              <Skeleton variant="text" className="w-24 sm:w-32 h-12" />
              <Skeleton variant="text" className="w-24 sm:w-32 h-12" />
            </>
          ) : (
            <>
              {/* PayIn Wallet Card */}
              <div className="relative px-2.5 sm:px-3 py-1.5 sm:py-2 border border-green-500 rounded-lg transition-all min-w-[100px] sm:min-w-[115px] overflow-hidden">
                {/* Wallet Icon Background - Right Aligned */}
                <div className="absolute inset-0 flex items-center justify-end pr-2 opacity-10">
                  <Wallet size={40} className="text-green-500" />
                </div>
                {/* Card Content */}
                <div className="relative z-10">
                  <div className="text-[8px] sm:text-[9px] text-green-600 font-semibold uppercase tracking-wide mb-0.5">
                    PayIn
                  </div>
                  <div className="text-xs sm:text-sm font-bold text-green-700 leading-tight">
                    {formatCurrency(parseFloat(merchantData?.payInWalletBalance || 0))}
                  </div>
                </div>
              </div>

              {/* Payout Wallet Card */}
              <div className="relative px-2.5 sm:px-3 py-1.5 sm:py-2 border border-blue-500 rounded-lg transition-all min-w-[100px] sm:min-w-[115px] overflow-hidden">
                {/* Wallet Icon Background - Right Aligned */}
                <div className="absolute inset-0 flex items-center justify-end pr-2 opacity-10">
                  <Wallet size={40} className="text-blue-500" />
                </div>
                {/* Card Content */}
                <div className="relative z-10">
                  <div className="text-[8px] sm:text-[9px] text-blue-600 font-semibold uppercase tracking-wide mb-0.5">
                    Payout
                  </div>
                  <div className="text-xs sm:text-sm font-bold text-blue-700 leading-tight">
                    {formatCurrency(parseFloat(merchantData?.payOutWalletBalance || 0))}
                  </div>
                </div>
              </div>

              {/* Funds on hold (admin) */}
              <div className="relative px-2.5 sm:px-3 py-1.5 sm:py-2 border border-amber-500 rounded-lg transition-all min-w-[100px] sm:min-w-[115px] overflow-hidden">
                <div className="absolute inset-0 flex items-center justify-end pr-2 opacity-10">
                  <Lock size={40} className="text-amber-500" />
                </div>
                <div className="relative z-10">
                  <div className="text-[8px] sm:text-[9px] text-amber-700 font-semibold uppercase tracking-wide mb-0.5">
                    On hold
                  </div>
                  <div className="text-xs sm:text-sm font-bold text-amber-800 leading-tight">
                    {formatCurrency(parseHeldAmount(merchantData?.heldAmount))}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  )
}

export default Header

